/**
 * Shape geometry, shared by the renderer, the picker previews and the server's
 * op validation.
 *
 * Every shape is built inside a unit box and then mapped onto whatever
 * rectangle the user dragged out, so one code path covers drawing to a canvas
 * and emitting an SVG `d` string for the little previews in the picker.
 *
 * Paths are emitted through a tiny "sink" interface — `moveTo`, `lineTo`,
 * `bezierCurveTo`, `closePath`. A CanvasRenderingContext2D satisfies it as-is.
 * Ellipses are approximated with four beziers rather than `ctx.ellipse` so SVG
 * gets the same geometry without needing arc commands.
 */

/** Bezier circle constant: handle length for a quarter turn. */
const K = 0.5522847498;

export const SHAPE_GROUPS = [
  {
    id: 'lines',
    label: 'Lines',
    shapes: ['line', 'arrow', 'double-arrow'],
  },
  {
    id: 'basic',
    label: 'Basic',
    shapes: ['rect', 'round-rect', 'ellipse', 'triangle', 'right-triangle', 'diamond', 'parallelogram', 'trapezium'],
  },
  {
    id: 'polygons',
    label: 'Polygons',
    shapes: ['pentagon', 'hexagon', 'heptagon', 'octagon'],
  },
  {
    id: 'stars',
    label: 'Stars & marks',
    shapes: ['star', 'star-6', 'burst', 'heart', 'lightning', 'cross', 'chevron', 'block-arrow'],
  },
  {
    id: 'callouts',
    label: 'Callouts',
    shapes: ['speech-bubble', 'cloud', 'crescent', 'moon-gibbous'],
  },
];

export const SHAPE_LABELS = {
  line: 'Line',
  arrow: 'Arrow',
  'double-arrow': 'Double arrow',
  rect: 'Rectangle',
  'round-rect': 'Rounded box',
  ellipse: 'Ellipse',
  triangle: 'Triangle',
  'right-triangle': 'Right triangle',
  diamond: 'Diamond',
  parallelogram: 'Parallelogram',
  trapezium: 'Trapezium',
  pentagon: 'Pentagon',
  hexagon: 'Hexagon',
  heptagon: 'Heptagon',
  octagon: 'Octagon',
  star: 'Star',
  'star-6': 'Six-point star',
  burst: 'Burst',
  heart: 'Heart',
  lightning: 'Lightning',
  cross: 'Cross',
  chevron: 'Chevron',
  'block-arrow': 'Block arrow',
  'speech-bubble': 'Speech bubble',
  cloud: 'Cloud',
  crescent: 'Crescent',
  'moon-gibbous': 'Half moon',
};

export const SHAPE_IDS = SHAPE_GROUPS.flatMap((g) => g.shapes);
const SHAPE_SET = new Set(SHAPE_IDS);

export const isShape = (id) => SHAPE_SET.has(id);

/** Shapes drawn as an open stroke; they are never filled. */
const OPEN = new Set(['line', 'arrow', 'double-arrow']);
export const isOpenShape = (id) => OPEN.has(id);

/** Regular n-gon inscribed in the unit box, first vertex pointing up. */
function regular(n, rotation = -Math.PI / 2) {
  const pts = [];
  for (let i = 0; i < n; i += 1) {
    const a = rotation + (i * Math.PI * 2) / n;
    pts.push([0.5 + Math.cos(a) * 0.5, 0.5 + Math.sin(a) * 0.5]);
  }
  return pts;
}

/** Alternating outer/inner points, for stars and bursts. */
function star(points, innerRatio, rotation = -Math.PI / 2) {
  const pts = [];
  for (let i = 0; i < points * 2; i += 1) {
    const r = (i % 2 === 0 ? 0.5 : 0.5 * innerRatio);
    const a = rotation + (i * Math.PI) / points;
    pts.push([0.5 + Math.cos(a) * r, 0.5 + Math.sin(a) * r]);
  }
  return pts;
}

/** Straight-edged shapes, as unit-box polygons. */
const POLYGONS = {
  triangle: [[0.5, 0], [1, 1], [0, 1]],
  'right-triangle': [[0, 0], [0, 1], [1, 1]],
  diamond: [[0.5, 0], [1, 0.5], [0.5, 1], [0, 0.5]],
  parallelogram: [[0.25, 0], [1, 0], [0.75, 1], [0, 1]],
  trapezium: [[0.22, 0], [0.78, 0], [1, 1], [0, 1]],
  pentagon: regular(5),
  hexagon: regular(6),
  heptagon: regular(7),
  octagon: regular(8, -Math.PI / 2 + Math.PI / 8),
  star: star(5, 0.395),
  'star-6': star(6, 0.55),
  burst: star(12, 0.74),
  cross: [
    [0.34, 0], [0.66, 0], [0.66, 0.34], [1, 0.34], [1, 0.66], [0.66, 0.66],
    [0.66, 1], [0.34, 1], [0.34, 0.66], [0, 0.66], [0, 0.34], [0.34, 0.34],
  ],
  chevron: [[0, 0], [0.55, 0], [1, 0.5], [0.55, 1], [0, 1], [0.45, 0.5]],
  'block-arrow': [
    [0, 0.3], [0.6, 0.3], [0.6, 0.06], [1, 0.5], [0.6, 0.94], [0.6, 0.7], [0, 0.7],
  ],
  lightning: [
    [0.62, 0], [0.2, 0.55], [0.45, 0.55], [0.34, 1], [0.82, 0.4], [0.55, 0.4], [0.72, 0],
  ],
};

/**
 * Emit `shape` into `sink`, mapped onto the rectangle at (x, y) size (w, h).
 * Coordinates may be negative width/height; callers normalise beforehand.
 */
export function buildShapePath(shape, x, y, w, h, sink) {
  const px = (u) => x + u * w;
  const py = (v) => y + v * h;

  const polygon = POLYGONS[shape];
  if (polygon) {
    sink.moveTo(px(polygon[0][0]), py(polygon[0][1]));
    for (let i = 1; i < polygon.length; i += 1) sink.lineTo(px(polygon[i][0]), py(polygon[i][1]));
    sink.closePath();
    return;
  }

  switch (shape) {
    case 'line':
      sink.moveTo(x, y);
      sink.lineTo(x + w, y + h);
      break;

    case 'arrow':
    case 'double-arrow': {
      // The head size is handled by the renderer, which knows the stroke width.
      sink.moveTo(x, y);
      sink.lineTo(x + w, y + h);
      break;
    }

    case 'rect':
      sink.moveTo(x, y);
      sink.lineTo(x + w, y);
      sink.lineTo(x + w, y + h);
      sink.lineTo(x, y + h);
      sink.closePath();
      break;

    case 'round-rect': {
      const r = Math.min(Math.abs(w), Math.abs(h)) * 0.18;
      const sx = Math.sign(w) || 1;
      const sy = Math.sign(h) || 1;
      const rx = r * sx;
      const ry = r * sy;
      sink.moveTo(x + rx, y);
      sink.lineTo(x + w - rx, y);
      sink.bezierCurveTo(x + w - rx * (1 - K), y, x + w, y + ry * (1 - K), x + w, y + ry);
      sink.lineTo(x + w, y + h - ry);
      sink.bezierCurveTo(x + w, y + h - ry * (1 - K), x + w - rx * (1 - K), y + h, x + w - rx, y + h);
      sink.lineTo(x + rx, y + h);
      sink.bezierCurveTo(x + rx * (1 - K), y + h, x, y + h - ry * (1 - K), x, y + h - ry);
      sink.lineTo(x, y + ry);
      sink.bezierCurveTo(x, y + ry * (1 - K), x + rx * (1 - K), y, x + rx, y);
      sink.closePath();
      break;
    }

    case 'ellipse': {
      const cx = x + w / 2;
      const cy = y + h / 2;
      const rx = w / 2;
      const ry = h / 2;
      sink.moveTo(cx + rx, cy);
      sink.bezierCurveTo(cx + rx, cy + ry * K, cx + rx * K, cy + ry, cx, cy + ry);
      sink.bezierCurveTo(cx - rx * K, cy + ry, cx - rx, cy + ry * K, cx - rx, cy);
      sink.bezierCurveTo(cx - rx, cy - ry * K, cx - rx * K, cy - ry, cx, cy - ry);
      sink.bezierCurveTo(cx + rx * K, cy - ry, cx + rx, cy - ry * K, cx + rx, cy);
      sink.closePath();
      break;
    }

    case 'heart':
      sink.moveTo(px(0.5), py(0.96));
      sink.bezierCurveTo(px(0.12), py(0.64), px(0), py(0.42), px(0), py(0.28));
      sink.bezierCurveTo(px(0), py(0.08), px(0.18), py(0.0), px(0.31), py(0.04));
      sink.bezierCurveTo(px(0.41), py(0.08), px(0.47), py(0.15), px(0.5), py(0.22));
      sink.bezierCurveTo(px(0.53), py(0.15), px(0.59), py(0.08), px(0.69), py(0.04));
      sink.bezierCurveTo(px(0.82), py(0.0), px(1), py(0.08), px(1), py(0.28));
      sink.bezierCurveTo(px(1), py(0.42), px(0.88), py(0.64), px(0.5), py(0.96));
      sink.closePath();
      break;

    case 'cloud':
      sink.moveTo(px(0.24), py(0.86));
      sink.bezierCurveTo(px(0.08), py(0.86), px(0), py(0.74), px(0.05), py(0.62));
      sink.bezierCurveTo(px(-0.02), py(0.46), px(0.10), py(0.34), px(0.24), py(0.38));
      sink.bezierCurveTo(px(0.29), py(0.20), px(0.50), py(0.16), px(0.59), py(0.30));
      sink.bezierCurveTo(px(0.71), py(0.16), px(0.93), py(0.24), px(0.91), py(0.44));
      sink.bezierCurveTo(px(1.03), py(0.52), px(1.00), py(0.76), px(0.85), py(0.82));
      sink.bezierCurveTo(px(0.80), py(0.90), px(0.66), py(0.92), px(0.60), py(0.86));
      sink.closePath();
      break;

    case 'speech-bubble': {
      const r = Math.min(Math.abs(w), Math.abs(h)) * 0.16;
      const rx = r * (Math.sign(w) || 1);
      const ry = r * (Math.sign(h) || 1);
      const body = y + h * 0.78;
      sink.moveTo(x + rx, y);
      sink.lineTo(x + w - rx, y);
      sink.bezierCurveTo(x + w - rx * (1 - K), y, x + w, y + ry * (1 - K), x + w, y + ry);
      sink.lineTo(x + w, body - ry);
      sink.bezierCurveTo(x + w, body - ry * (1 - K), x + w - rx * (1 - K), body, x + w - rx, body);
      // Tail, dropping from the lower-left of the body.
      sink.lineTo(px(0.36), body);
      sink.lineTo(px(0.22), y + h);
      sink.lineTo(px(0.24), body);
      sink.lineTo(x + rx, body);
      sink.bezierCurveTo(x + rx * (1 - K), body, x, body - ry * (1 - K), x, body - ry);
      sink.lineTo(x, y + ry);
      sink.bezierCurveTo(x, y + ry * (1 - K), x + rx * (1 - K), y, x + rx, y);
      sink.closePath();
      break;
    }

    case 'crescent':
      sink.moveTo(px(0.72), py(0.06));
      sink.bezierCurveTo(px(0.26), py(0.14), px(0.10), py(0.72), px(0.52), py(0.96));
      sink.bezierCurveTo(px(0.30), py(0.66), px(0.36), py(0.28), px(0.72), py(0.06));
      sink.closePath();
      break;

    case 'moon-gibbous':
      sink.moveTo(px(0.5), py(0.02));
      sink.bezierCurveTo(px(0.78), py(0.02), px(1), py(0.24), px(1), py(0.5));
      sink.bezierCurveTo(px(1), py(0.76), px(0.78), py(0.98), px(0.5), py(0.98));
      sink.bezierCurveTo(px(0.70), py(0.78), px(0.70), py(0.22), px(0.5), py(0.02));
      sink.closePath();
      break;

    default:
      // Unknown shape: fall back to a plain box rather than drawing nothing.
      sink.moveTo(x, y);
      sink.lineTo(x + w, y);
      sink.lineTo(x + w, y + h);
      sink.lineTo(x, y + h);
      sink.closePath();
  }
}

/** Accumulates an SVG `d` string, for the picker previews. */
class SvgSink {
  constructor() {
    this.parts = [];
  }

  #n(v) {
    return Math.round(v * 100) / 100;
  }

  moveTo(x, y) { this.parts.push(`M${this.#n(x)} ${this.#n(y)}`); }

  lineTo(x, y) { this.parts.push(`L${this.#n(x)} ${this.#n(y)}`); }

  bezierCurveTo(a, b, c, d, e, f) {
    this.parts.push(`C${this.#n(a)} ${this.#n(b)} ${this.#n(c)} ${this.#n(d)} ${this.#n(e)} ${this.#n(f)}`);
  }

  closePath() { this.parts.push('Z'); }

  toString() { return this.parts.join(' '); }
}

/** SVG path data for a shape drawn inside the given box. */
export function shapePathData(shape, x, y, w, h) {
  const sink = new SvgSink();
  buildShapePath(shape, x, y, w, h, sink);
  return sink.toString();
}
